import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  ArrowForward,
  CalendarMonth,
  CalendarViewMonth,
  ChevronLeft,
  ChevronRight,
  ContentCopy,
  Delete,
  Edit,
  EventAvailable,
  FormatListBulleted,
  Luggage,
  Person,
  ScubaDiving,
  Sync,
} from '@mui/icons-material';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import AppNavigation from './AppNavigation';
import SignInNudge from './SignInNudge';
import ScheduleDiveDialog from './ScheduleDiveDialog';
import { useNotification } from './NotificationContext';
import { useShareLatestDive } from '../hooks/useShareLatestDive';
import { scheduledDivesService } from '../services/scheduledDivesService';
import { diveService } from '../services/diveService';
import { authService } from '../services/authService';
import { authService as suuntoAuthService } from '../services/api';
import { useDiveSiteCatalog } from '../hooks/useDiveSiteCatalog';
import { useDiveLocations } from '../hooks/useDiveLocations';
import { userDataService } from '../services/userDataService';
import { downloadIcs } from '../utils/icsExport';
import { getTimezoneFromCoordinates } from '../utils/timezoneUtils';
import type { ScheduledDive } from '../types/ScheduledDive';
import type { DiveSite } from '../types/DiveSite';
import type { Dive } from '../types/Dive';
import { getSuuntoRedirectUri, openAuthUrl } from '../utils/native';

// For date-only values like scheduledDate: slices the date portion from the ISO
// string before parsing so that local-timezone conversion never shifts the date
// to the previous day. Do NOT use this on startTime — see parseDiveStartDay.
const parseDiveDate = (iso: string) => parseISO(iso.slice(0, 10));

// A dive's startTime is a real UTC instant, so slicing its ISO string would
// bucket it by the UTC calendar day: an 8pm EDT night dive is already past
// midnight UTC and would land on the following day. Bucket by the calendar day
// at the dive site instead, falling back to the viewer's zone when the dive has
// no coordinates.
const calendarDayInZone = (iso: string, timeZone?: string) =>
  parseISO(
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date(iso))
  );

const parseDiveStartDay = (dive: Dive) =>
  calendarDayInZone(
    dive.startTime,
    dive.latitude != null && dive.longitude != null
      ? getTimezoneFromCoordinates(dive.latitude, dive.longitude)
      : undefined
  );

type ViewMode = 'month' | 'agenda';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DIFFICULTY_COLORS: Record<string, 'success' | 'warning' | 'error'> = {
  Beginner: 'success',
  Intermediate: 'warning',
  Advanced: 'error',
};

const isPastDive = (scheduledDate: string) =>
  isBefore(parseDiveDate(scheduledDate), startOfDay(new Date()));

const DiveCalendar: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { offerShare, prompt: sharePrompt } = useShareLatestDive();
  const [currentUser, setCurrentUser] = useState(authService.getUser());
  const { allSites } = useDiveSiteCatalog();
  const { getLocation } = useDiveLocations();

  const [suuntoAccessToken] = useState<string | null>(() =>
    localStorage.getItem('suunto_access_token')
  );
  const hasSuuntoAccess = !!(currentUser?.hasSuuntoConnection || suuntoAccessToken);
  const [syncing, setSyncing] = useState(false);

  const loggedDiveLabel = (ld: Dive): string => {
    if (ld.diveName?.trim()) return ld.diveName.trim();
    if (ld.latitude && ld.longitude) {
      const key = userDataService.generateDiveKey(ld.latitude, ld.longitude, new Date(ld.startTime));
      const saved = getLocation(key, ld.latitude, ld.longitude);
      if (saved?.locationName?.trim()) return saved.locationName.trim();
    }
    if (ld.location && !ld.location.includes('°')) return ld.location;
    return ld.description?.trim() || 'Dive';
  };

  const getLinkedSite = (dive: ScheduledDive): DiveSite | null => {
    const slug = dive.extra?.linkedSiteSlug;
    if (!slug) return null;
    return allSites.find((s) => s.slug === slug) ?? null;
  };

  const [dives, setDives] = useState<ScheduledDive[]>([]);
  const [loggedDives, setLoggedDives] = useState<Dive[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDive, setEditingDive] = useState<ScheduledDive | null>(null);
  const [scheduleDate, setScheduleDate] = useState<string | null>(null);

  const openScheduleDialog = (date: string | null) => {
    setEditingDive(null);
    setScheduleDate(date);
    setDialogOpen(true);
  };

  const [detailDive, setDetailDive] = useState<ScheduledDive | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ScheduledDive | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    try {
      setError(null);
      const [scheduled, logged] = await Promise.all([
        scheduledDivesService.getMyScheduledDives(),
        diveService.getMyDives().catch(() => [] as Dive[]), // graceful — no Suunto = empty
      ]);
      setDives(scheduled);
      setLoggedDives(logged);
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const handleReconnectSuunto = async () => {
    try {
      const redirectUri = getSuuntoRedirectUri();
      const response = await suuntoAuthService.getAuthUrl(redirectUri);
      if (response.authUrl?.includes('your-suunto-client-id-here')) {
        throw new Error('Suunto API credentials are not configured.');
      }
      if (response.authUrl) {
        await openAuthUrl(response.authUrl);
      }
    } catch (err: any) {
      showError(err?.message ?? 'Failed to connect to Suunto.');
    }
  };

  const handleSyncFromSuunto = async () => {
    setSyncing(true);
    try {
      // Connect the Suunto account first if we have a token but no server-side link yet
      if (!currentUser?.hasSuuntoConnection && suuntoAccessToken) {
        const connectResponse = await diveService.connectSuuntoAccount(suuntoAccessToken);
        if (connectResponse.user) {
          setCurrentUser(connectResponse.user);
        }
      }

      const result = await diveService.syncFromSuunto();
      await load();
      showSuccess(
        `Synced ${result.totalDives} dive${result.totalDives === 1 ? '' : 's'} ` +
        `(${result.newDives} new, ${result.updatedDives} updated).`
      );
      if (result.newDives > 0) void offerShare();
    } catch (err: any) {
      const message: string = err?.message ?? 'Failed to sync dives from Suunto.';
      // If the Suunto session expired, offer to re-authorize instead of just failing.
      if (/reconnect|expired|connect to suunto|unauthor/i.test(message)) {
        showError('Your Suunto session has expired — reconnecting…');
        await handleReconnectSuunto();
      } else {
        showError(message);
      }
    } finally {
      setSyncing(false);
    }
  };

  // Find logged dives that happened on the same date as a scheduled dive.
  // Matched on the viewer's local day rather than the dive site's, so a dive
  // planned and logged on the same day still pairs up when diving abroad.
  const matchedLoggedDives = (scheduledDate: string): Dive[] =>
    loggedDives.filter((d) =>
      isSameDay(calendarDayInZone(d.startTime), parseDiveDate(scheduledDate))
    );

  const handleSaved = (saved: ScheduledDive) => {
    setDives((prev) => {
      const idx = prev.findIndex((d) => d.diveId === saved.diveId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved].sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
    });
    showSuccess(editingDive ? 'Dive updated.' : 'Dive scheduled!');
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await scheduledDivesService.deleteScheduledDive(deleteConfirm.diveId);
      setDives((prev) => prev.filter((d) => d.diveId !== deleteConfirm.diveId));
      showSuccess('Dive removed from calendar.');
      setDeleteConfirm(null);
      setDetailDive(null);
    } catch (err: any) {
      showError(err?.message ?? 'Failed to delete dive.');
    } finally {
      setDeleting(false);
    }
  };

  const copyInviteLink = (dive: ScheduledDive) => {
    const url = scheduledDivesService.buildInviteUrl(dive.inviteCode);
    navigator.clipboard.writeText(url)
      .then(() => showSuccess('Invite link copied!'))
      .catch(() => showError('Could not copy link.'));
  };

  const handleDownloadIcs = (dive: ScheduledDive) => {
    const url = scheduledDivesService.buildInviteUrl(dive.inviteCode);
    downloadIcs(dive, url);
  };

  const isOwner = (dive: ScheduledDive) => dive.ownerUserId === currentUser?.userId;

  // Logged dive IDs already shown inline on a matched scheduled dive — don't double-render
  const claimedLoggedDiveIds = new Set(
    dives.flatMap((d) => matchedLoggedDives(d.scheduledDate).map((ld) => ld.id))
  );

  const divesForDay = (day: Date) =>
    dives.filter((d) => isSameDay(parseDiveDate(d.scheduledDate), day));

  const bySessionOrder = (a: ScheduledDive, b: ScheduledDive) =>
    Number(a.extra?.sessionOrder ?? 0) - Number(b.extra?.sessionOrder ?? 0);

  // All dives sharing this dive's sessionId (incl. itself), in session order
  const sessionDivesOf = (dive: ScheduledDive): ScheduledDive[] => {
    const sid = dive.extra?.sessionId;
    if (!sid) return [dive];
    return dives.filter((d) => d.extra?.sessionId === sid).sort(bySessionOrder);
  };

  // Group a day's dives so paired session dives render as one calendar entry
  const groupBySession = (dayDives: ScheduledDive[]): ScheduledDive[][] => {
    const groups: ScheduledDive[][] = [];
    const seen = new Map<string, ScheduledDive[]>();
    for (const d of dayDives) {
      const sid = d.extra?.sessionId;
      if (sid) {
        let group = seen.get(sid);
        if (!group) {
          group = [];
          seen.set(sid, group);
          groups.push(group);
        }
        group.push(d);
      } else {
        groups.push([d]);
      }
    }
    groups.forEach((g) => g.sort(bySessionOrder));
    return groups;
  };

  const unclaimedLoggedDivesForDay = (day: Date) =>
    loggedDives.filter(
      (ld) => isSameDay(parseDiveStartDay(ld), day) && !claimedLoggedDiveIds.has(ld.id)
    );

  // --- Month grid ---
  const calendarDays = eachDayOfInterval({
    start: startOfWeek(startOfMonth(currentMonth)),
    end: endOfWeek(endOfMonth(currentMonth)),
  });

  // --- Agenda: all dives from start of current month, session dives kept adjacent ---
  const agendaDives = [...dives]
    .filter((d) => parseDiveDate(d.scheduledDate) >= startOfMonth(currentMonth))
    .sort((a, b) =>
      a.scheduledDate.localeCompare(b.scheduledDate) ||
      (a.extra?.sessionId ?? a.diveId).localeCompare(b.extra?.sessionId ?? b.diveId) ||
      bySessionOrder(a, b)
    )
    .slice(0, 50);

  if (loading) {
    return (
      <Box>
        <AppNavigation />
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      </Box>
    );
  }

  return (
    <Box>
      <AppNavigation />

      {/* Header bar */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => setCurrentMonth((m) => addMonths(m, -1))} size="small">
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" fontWeight={700} sx={{ minWidth: 160, textAlign: 'center' }}>
            {format(currentMonth, 'MMMM yyyy')}
          </Typography>
          <IconButton onClick={() => setCurrentMonth((m) => addMonths(m, 1))} size="small">
            <ChevronRight />
          </IconButton>
          <Button size="small" onClick={() => setCurrentMonth(new Date())} sx={{ ml: 0.5 }}>
            Today
          </Button>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="month" aria-label="month view">
              <Tooltip title="Month view"><CalendarViewMonth fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="agenda" aria-label="agenda view">
              <Tooltip title="Agenda view"><FormatListBulleted fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          {currentUser && hasSuuntoAccess && (
            <Button
              variant="outlined"
              startIcon={syncing ? <CircularProgress size={16} /> : <Sync />}
              onClick={handleSyncFromSuunto}
              disabled={syncing}
              size="small"
            >
              {syncing ? 'Syncing…' : 'Sync with Suunto'}
            </Button>
          )}

          {currentUser && (
            <Button
              variant="outlined"
              startIcon={<Luggage />}
              onClick={() => navigate('/trips')}
              size="small"
            >
              Trips
            </Button>
          )}

          {currentUser && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => openScheduleDialog(null)}
              size="small"
            >
              Schedule Dive
            </Button>
          )}
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!currentUser && (
        <SignInNudge
          feature="schedule dives"
          description="Plan upcoming dives, invite your buddies, and see your logged dives on the calendar — sign in or create a free account to get started."
          onSignedIn={() => setCurrentUser(authService.getUser())}
        />
      )}

      {/* ---- MONTH VIEW ---- */}
      {viewMode === 'month' && (
        <Box>
          <Grid container columns={7} sx={{ mb: 0.5 }}>
            {DAYS.map((d) => (
              <Grid item xs={1} key={d}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 0.5 }}>
                  {d}
                </Typography>
              </Grid>
            ))}
          </Grid>

          <Grid container columns={7} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
            {calendarDays.map((day) => {
              const dayDives = divesForDay(day);
              const dayLogged = unclaimedLoggedDivesForDay(day);
              const inMonth = isSameMonth(day, currentMonth);
              const today = isToday(day);

              return (
                <Grid
                  item
                  xs={1}
                  key={day.toISOString()}
                  onClick={() => {
                    if (currentUser) {
                      openScheduleDialog(format(day, 'yyyy-MM-dd'));
                    }
                  }}
                  sx={{
                    minHeight: 90,
                    borderRight: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: !inMonth ? 'action.hover' : today ? 'primary.50' : 'background.paper',
                    p: 0.5,
                    opacity: inMonth ? 1 : 0.45,
                    '&:nth-of-type(7n)': { borderRight: 0 },
                    ...(currentUser && {
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.selected' },
                    }),
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={today ? 700 : 400}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: today ? 'primary.main' : 'transparent',
                      color: today ? 'primary.contrastText' : 'text.primary',
                      mb: 0.25,
                    }}
                  >
                    {format(day, 'd')}
                  </Typography>

                  <Stack spacing={0.25}>
                    {groupBySession(dayDives).slice(0, 3).map((group) => (
                      <Box
                        key={group[0].diveId}
                        onClick={(e) => { e.stopPropagation(); setDetailDive(group[0]); }}
                        sx={{
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 0.75,
                          backgroundColor: isOwner(group[0]) ? 'primary.main' : 'secondary.main',
                          color: '#fff !important',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                        }}
                      >
                        {group.map((d) => d.siteName || d.charterCompany || 'Dive').join(' + ')}
                      </Box>
                    ))}
                    {groupBySession(dayDives).length > 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        +{groupBySession(dayDives).length - 3} more
                      </Typography>
                    )}
                    {dayLogged.map((ld) => (
                      <Box
                        key={ld.id}
                        onClick={(e) => { e.stopPropagation(); navigate(`/dive/${ld.id}`); }}
                        sx={{
                          px: 0.5,
                          py: 0.25,
                          borderRadius: 0.75,
                          backgroundColor: 'success.dark',
                          color: '#fff !important',
                          cursor: 'pointer',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                        }}
                      >
                        {loggedDiveLabel(ld)}
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* ---- AGENDA VIEW ---- */}
      {viewMode === 'agenda' && (
        <Stack spacing={2}>
          {agendaDives.length === 0 && (
            <Box textAlign="center" py={6}>
              <CalendarMonth sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No upcoming dives scheduled.</Typography>
              {currentUser && (
                <Button variant="outlined" startIcon={<Add />} sx={{ mt: 2 }} onClick={() => openScheduleDialog(null)}>
                  Schedule your first dive
                </Button>
              )}
            </Box>
          )}
          {agendaDives.map((dive) => (
            <DiveAgendaCard
              key={dive.diveId}
              dive={dive}
              sessionDives={sessionDivesOf(dive)}
              linkedSite={getLinkedSite(dive)}
              loggedDives={matchedLoggedDives(dive.scheduledDate)}
              isOwner={isOwner(dive)}
              onEdit={() => { setEditingDive(dive); setDialogOpen(true); }}
              onDelete={() => setDeleteConfirm(dive)}
              onCopyLink={() => copyInviteLink(dive)}
              onAddToDeviceCalendar={() => handleDownloadIcs(dive)}
              onClick={() => setDetailDive(dive)}
              onViewSite={(slug) => navigate(`/dive-sites/${slug}`)}
              onViewLoggedDive={(id) => navigate(`/dive/${id}`)}
              loggedDiveLabel={loggedDiveLabel}
            />
          ))}
        </Stack>
      )}

      {/* ---- SCHEDULE DIALOG ---- */}
      <ScheduleDiveDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
        editing={editingDive}
        initialDate={scheduleDate}
      />

      {/* ---- DETAIL DIALOG ---- */}
      {detailDive && (
        <DiveDetailDialog
          dive={detailDive}
          sessionDives={sessionDivesOf(detailDive)}
          onViewSessionDive={(d) => setDetailDive(d)}
          linkedSite={getLinkedSite(detailDive)}
          loggedDives={matchedLoggedDives(detailDive.scheduledDate)}
          isOwner={isOwner(detailDive)}
          onClose={() => setDetailDive(null)}
          onEdit={() => { setEditingDive(detailDive); setDetailDive(null); setDialogOpen(true); }}
          onDelete={() => { setDeleteConfirm(detailDive); setDetailDive(null); }}
          onCopyLink={() => copyInviteLink(detailDive)}
          onAddToDeviceCalendar={() => handleDownloadIcs(detailDive)}
          onViewSite={(slug) => navigate(`/dive-sites/${slug}`)}
          onViewLoggedDive={(id) => { setDetailDive(null); navigate(`/dive/${id}`); }}
          loggedDiveLabel={loggedDiveLabel}
        />
      )}

      {/* ---- DELETE CONFIRM ---- */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Scheduled Dive?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the dive on{' '}
            <strong>{deleteConfirm ? format(parseDiveDate(deleteConfirm.scheduledDate), 'MMMM d, yyyy') : ''}</strong>
            {deleteConfirm?.siteName ? ` at ${deleteConfirm.siteName}` : ''} from the calendar.
            {deleteConfirm && deleteConfirm.invitees.length > 0 && ' Accepted guests will no longer see it.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
      {sharePrompt}
    </Box>
  );
};

// ---- Sub-components ----

interface AgendaCardProps {
  dive: ScheduledDive;
  sessionDives: ScheduledDive[];
  linkedSite: DiveSite | null;
  loggedDives: Dive[];
  isOwner: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onCopyLink: () => void;
  onAddToDeviceCalendar: () => void;
  onClick: () => void;
  onViewSite: (slug: string) => void;
  onViewLoggedDive: (id: string) => void;
  loggedDiveLabel: (ld: Dive) => string;
}

const DiveAgendaCard: React.FC<AgendaCardProps> = ({
  dive, sessionDives, linkedSite, loggedDives, isOwner,
  onEdit, onDelete, onCopyLink, onAddToDeviceCalendar, onClick, onViewSite, onViewLoggedDive, loggedDiveLabel,
}) => {
  const accepted = dive.invitees.filter((i) => i.status === 'accepted');
  const past = isPastDive(dive.scheduledDate);
  const sessionIndex = sessionDives.findIndex((d) => d.diveId === dive.diveId);

  return (
    <Card variant="outlined">
      <CardActionArea onClick={onClick}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
            <Box flex={1} minWidth={0}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {format(parseDiveDate(dive.scheduledDate), 'EEEE, MMMM d, yyyy')}
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} noWrap>
                {dive.siteName || dive.charterCompany || 'Dive'}
              </Typography>
              {dive.siteName && dive.charterCompany && (
                <Typography variant="body2" color="text.secondary" noWrap>
                  {dive.charterCompany}
                </Typography>
              )}
              {linkedSite && (
                <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                  <Chip label={linkedSite.siteType.replace('-', ' ')} size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                  <Chip label={linkedSite.difficulty} size="small" color={DIFFICULTY_COLORS[linkedSite.difficulty]} sx={{ fontSize: '0.65rem', height: 18 }} />
                  {linkedSite.region && (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      {linkedSite.region}
                    </Typography>
                  )}
                </Stack>
              )}
              {dive.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {dive.notes}
                </Typography>
              )}
              {/* Past: show matched logged dives */}
              {past && loggedDives.length > 0 && (
                <Stack spacing={0.25} mt={0.75}>
                  {loggedDives.map((ld) => (
                    <Stack key={ld.id} direction="row" alignItems="center" spacing={0.5}
                      onClick={(e) => { e.stopPropagation(); onViewLoggedDive(ld.id); }}
                      sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                    >
                      <ScubaDiving sx={{ fontSize: 14, color: 'primary.main' }} />
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        Logged: {ld.maxDepth}m · {ld.duration} · {loggedDiveLabel(ld)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
              {accepted.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.5} mt={0.75}>
                  <AvatarGroup max={5} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.7rem' } }}>
                    {accepted.map((inv) => (
                      <Tooltip key={inv.userId} title={inv.displayName}>
                        <Avatar sx={{ bgcolor: 'secondary.main', width: 24, height: 24, fontSize: '0.7rem' }}>
                          {inv.displayName.charAt(0).toUpperCase()}
                        </Avatar>
                      </Tooltip>
                    ))}
                  </AvatarGroup>
                  <Typography variant="caption" color="text.secondary">
                    {accepted.length} going
                  </Typography>
                </Stack>
              )}
            </Box>
            <Stack alignItems="flex-end" spacing={0.5} flexShrink={0}>
              {sessionDives.length > 1 && (
                <Chip label={`Trip dive ${sessionIndex + 1} of ${sessionDives.length}`} size="small" variant="outlined" />
              )}
              {isOwner && <Chip label="Organizer" size="small" color="primary" />}
              {!isOwner && <Chip label="Invited" size="small" color="secondary" />}
              {past && loggedDives.length > 0 && (
                <Chip label="Logged" size="small" color="success" icon={<ScubaDiving sx={{ fontSize: '0.8rem !important' }} />} />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
      <Divider />
      <Box px={2} py={0.75} display="flex" gap={1} flexWrap="wrap">
        <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={(e) => { e.stopPropagation(); onCopyLink(); }}>
          Invite link
        </Button>
        <Button size="small" startIcon={<EventAvailable fontSize="small" />} onClick={(e) => { e.stopPropagation(); onAddToDeviceCalendar(); }}>
          Add to calendar
        </Button>
        {linkedSite && (
          <Button size="small" endIcon={<ArrowForward fontSize="small" />} onClick={(e) => { e.stopPropagation(); onViewSite(linkedSite.slug); }}>
            Site details
          </Button>
        )}
        {isOwner && (
          <>
            <Button size="small" startIcon={<Edit fontSize="small" />} onClick={(e) => { e.stopPropagation(); onEdit(); }}>
              Edit
            </Button>
            <Button size="small" color="error" startIcon={<Delete fontSize="small" />} onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              Remove
            </Button>
          </>
        )}
      </Box>
    </Card>
  );
};

interface DetailDialogProps {
  dive: ScheduledDive;
  sessionDives: ScheduledDive[];
  onViewSessionDive: (dive: ScheduledDive) => void;
  linkedSite: DiveSite | null;
  loggedDives: Dive[];
  isOwner: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopyLink: () => void;
  onAddToDeviceCalendar: () => void;
  onViewSite: (slug: string) => void;
  onViewLoggedDive: (id: string) => void;
  loggedDiveLabel: (ld: Dive) => string;
}

const DiveDetailDialog: React.FC<DetailDialogProps> = ({
  dive, sessionDives, onViewSessionDive, linkedSite, loggedDives, isOwner,
  onClose, onEdit, onDelete, onCopyLink, onAddToDeviceCalendar, onViewSite, onViewLoggedDive, loggedDiveLabel,
}) => {
  const accepted = dive.invitees.filter((i) => i.status === 'accepted');
  const pending = dive.invitees.filter((i) => i.status === 'pending');
  const past = isPastDive(dive.scheduledDate);
  const sessionSiblings = sessionDives.filter((d) => d.diveId !== dive.diveId);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={700}>
          {dive.siteName || dive.charterCompany || 'Scheduled Dive'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {format(parseDiveDate(dive.scheduledDate), 'EEEE, MMMM d, yyyy')}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {dive.siteName && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>DIVE SITE</Typography>
              <Typography>{dive.siteName}</Typography>
            </Box>
          )}

          {linkedSite && (
            <Box sx={{ p: 1.5, borderRadius: 1.5, border: 1, borderColor: 'primary.main', bgcolor: 'primary.50' }}>
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  <Chip label={linkedSite.siteType.replace('-', ' ')} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                  <Chip label={linkedSite.difficulty} size="small" color={DIFFICULTY_COLORS[linkedSite.difficulty]} sx={{ fontSize: '0.7rem', height: 20 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {linkedSite.region}{linkedSite.country ? `, ${linkedSite.country}` : ''}
                  </Typography>
                </Stack>
                {linkedSite.summary && (
                  <Typography variant="caption" color="text.secondary">{linkedSite.summary}</Typography>
                )}
                {linkedSite.highlights.length > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    ✦ {linkedSite.highlights.slice(0, 4).join(' · ')}
                  </Typography>
                )}
                <Button
                  size="small"
                  endIcon={<ArrowForward fontSize="small" />}
                  onClick={() => { onClose(); onViewSite(linkedSite.slug); }}
                  sx={{ alignSelf: 'flex-start', p: 0, fontSize: '0.7rem' }}
                >
                  View full site details
                </Button>
              </Stack>
            </Box>
          )}

          {sessionSiblings.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                ALSO ON THIS TRIP ({sessionDives.length} dives total)
              </Typography>
              <Stack mt={0.5} spacing={0.5}>
                {sessionSiblings.map((sibling) => (
                  <Stack
                    key={sibling.diveId}
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    onClick={() => onViewSessionDive(sibling)}
                    sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                  >
                    <ScubaDiving sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                      {sibling.siteName || sibling.charterCompany || 'Dive'}
                    </Typography>
                    <ArrowForward sx={{ fontSize: 14 }} color="action" />
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {dive.charterCompany && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>CHARTER</Typography>
              <Typography>{dive.charterCompany}</Typography>
            </Box>
          )}
          {dive.notes && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>NOTES</Typography>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>{dive.notes}</Typography>
            </Box>
          )}

          {/* Logged dives for this date */}
          {past && loggedDives.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                LOGGED DIVE{loggedDives.length > 1 ? 'S' : ''} FROM THIS DAY
              </Typography>
              <Stack mt={0.75} spacing={1}>
                {loggedDives.map((ld) => (
                  <Card key={ld.id} variant="outlined" sx={{ borderColor: 'success.main' }}>
                    <CardActionArea onClick={() => onViewLoggedDive(ld.id)}>
                      <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={1} alignItems="center">
                            <ScubaDiving color="success" fontSize="small" />
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {loggedDiveLabel(ld)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {ld.maxDepth}m · {ld.duration}
                                {ld.diveName?.trim() ? ` · ${loggedDiveLabel({ ...ld, diveName: undefined })}` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                          <ArrowForward fontSize="small" color="action" />
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>ORGANIZER</Typography>
            <Stack direction="row" alignItems="center" spacing={0.75} mt={0.25}>
              <Person fontSize="small" color="action" />
              <Typography>{dive.ownerDisplayName}</Typography>
            </Stack>
          </Box>
          {accepted.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>GOING ({accepted.length})</Typography>
              <Stack mt={0.5} spacing={0.5}>
                {accepted.map((inv) => (
                  <Stack key={inv.userId} direction="row" alignItems="center" spacing={0.75}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'secondary.main' }}>
                      {inv.displayName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2">{inv.displayName}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
          {pending.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>INVITED (awaiting response)</Typography>
              <Stack mt={0.5} spacing={0.5}>
                {pending.map((inv) => (
                  <Stack key={inv.userId} direction="row" alignItems="center" spacing={0.75}>
                    <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'action.disabledBackground' }}>
                      {inv.displayName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" color="text.secondary">{inv.displayName}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        <Button startIcon={<ContentCopy />} onClick={onCopyLink}>Copy invite link</Button>
        <Button startIcon={<EventAvailable />} onClick={onAddToDeviceCalendar}>Add to calendar</Button>
        {isOwner && (
          <>
            <Button startIcon={<Edit />} onClick={onEdit}>Edit</Button>
            <Button color="error" startIcon={<Delete />} onClick={onDelete}>Remove</Button>
          </>
        )}
        <Box flex={1} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiveCalendar;
